import { getCurrentFacilityId } from "@risalabs_frontend_org/auth-wrapper";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { SpinningLoader } from "risa-oasis-ui_v2";
import { FirestoreService } from "../../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../../api/firebase/references";
import DynamicSingleSideTab from "../../components/dynamicSingleSideTab/dynamicSingleSideTab";
import { getReviewPendingNavEntriesForFacility } from "../../constants/reviewPendingNavConfig";
import { usePayerNameContextFromSingleOrderLayout } from "../../context/payerNameContextFromSingleOrderLayout";
import { useUserThemeContext } from "../../context/userThemeContext";
import { HealthCareFacilityModel } from "../../data-model/healthFacility";
import { ScreenNamesForBigQuery } from "../../enums/eventsData";
import { EventsName } from "../../enums/eventsName";
import { SideNavElementId } from "../../enums/worklistEnums";
import { fetchOncoemrUsersForOrg } from "../../redux/slice/oncoemrUsersSlice";
import { AppDispatch, RootState } from "../../redux/store/store";
import {
  UserGeneralInfoContextKeys,
  UserStore,
} from "../../store/UserGeneralInfoStore";
import ChevronDown from "../../svg/chevron-down";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { swapUnderscoreHyphen } from "../../utils/swapUnderscoreWithHypen";
import { apiWorkerManager } from "../../workerManagers/workerManager";
import { useRefreshFacetCountsOnRouteEntry } from "./hooks/useRefreshFacetCountsOnRouteEntry";
import { useSideNavElements } from "./hooks/useSideNavElements";
import { useSideNavOrderCounts } from "./hooks/useSideNavOrderCounts";

/**
 * Main worklists page component that renders a collapsible side navigation with
 * dynamic auth status tabs and order counts, alongside a routed outlet for the
 * active worklist table. Manages tab navigation, facet count fetching from Postgres,
 * and facility header display.
 */
const Worklists = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    showSideNav,
    setShowSideNav,
    showWorklistNavigation,
    setShowWorklistNavigation,
  } = useUserThemeContext();
  const { setPayerName } = usePayerNameContextFromSingleOrderLayout();

  const { activeTab, sideLinks, setActiveTab } = useSideNavElements();
  const [healthFacilityName, setHealthFacilityName] = useState<string>("");
  const [healthFacilityLoading, setHealthFacilityLoading] =
    useState<boolean>(false);
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const { data: providerInfo } = useSelector(
    (state: RootState) => state.providerDetails,
  );
  const { data: singleHealthFacilityDetails } = useSelector(
    (state: RootState) => state.singleHealthFacilityDetails,
  );

  const dispatch = useDispatch<AppDispatch>();

  const {
    counts: mapOfSideNavElementsIdAndCountOfOrders,
    loading: countsLoading,
  } = useSideNavOrderCounts();

  const reviewPendingCountLoading = useSelector((state: RootState) =>
    Object.values(state.reviewPendingCount.loadingByMasterAuthStatus).some(
      Boolean,
    ),
  );

  const reviewPendingSideNavIds = useMemo(
    () =>
      getReviewPendingNavEntriesForFacility(singleHealthFacilityDetails).map(
        (e) => e.id,
      ),
    [singleHealthFacilityDetails],
  );

  const sideNavElementsIdWhereCountIsRequired = [
    SideNavElementId.MyPaOrders,
    SideNavElementId.AuthRequired,
    SideNavElementId.Pending,
    SideNavElementId.Query,
    SideNavElementId.Hold,
    SideNavElementId.ReferralRequired,
    SideNavElementId.QuickReview,
    ...reviewPendingSideNavIds,
  ];

  useEffect(() => {
    const shouldShowSideNav = sideLinks.some((element) =>
      location.pathname.startsWith(`/worklists/${element.id}`),
    );

    setShowSideNav(shouldShowSideNav);
  }, [location, sideLinks]);

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
    apiWorkerManager.enqueueAndProcess({
      patient_id: "",
      org_id: getOrgIdForFetchExternalWorklist(),
      order_id: "",
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      event_name: EventsName.SIDE_NAV_CLICKED,
      additional_data: {
        screen_name: ScreenNamesForBigQuery.WORKLIST,
        source: swapUnderscoreHyphen(tabId),
      },
    });
    navigate(`/worklists/${tabId}`);
  };

  useEffect(() => {
    const pathAfterWorklists =
      location.pathname.replace(/^\/worklists\/?/, "") || "";
    const matched =
      pathAfterWorklists &&
      sideLinks.length > 0 &&
      sideLinks.some((link) => link.id === pathAfterWorklists);
    if (matched) {
      setActiveTab(pathAfterWorklists);
      apiWorkerManager.enqueueAndProcess({
        patient_id: "",
        org_id: getOrgIdForFetchExternalWorklist(),
        order_id: "",
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        event_name: EventsName.WORKLIST_SCREEN_SHOWN,
        additional_data: {
          screen_name: ScreenNamesForBigQuery.WORKLIST,
          selected_tab: swapUnderscoreHyphen(pathAfterWorklists),
          tab_name: swapUnderscoreHyphen(pathAfterWorklists),
        },
      });
    } else {
      const parentTableFromStore = UserStore.getItem(
        UserGeneralInfoContextKeys.PARENT_TABLE,
      );
      setActiveTab(
        parentTableFromStore ? parentTableFromStore : "medical-pa-orders",
      );
    }
  }, [location.pathname, sideLinks, user?.id, user?.email]);

  const worklistOrgId = useMemo(
    () => getOrgIdForFetchExternalWorklist(),
    [
      providerInfo?.FacilityId,
      singleHealthFacilityDetails?.corresponding_internal_org_id,
    ],
  );

  useRefreshFacetCountsOnRouteEntry(sideLinks);

  useEffect(() => {
    if (!providerInfo || !worklistOrgId) {
      return;
    }
    void dispatch(fetchOncoemrUsersForOrg(worklistOrgId));
  }, [dispatch, providerInfo, worklistOrgId]);

  useEffect(() => {
    getHealthFacilityName();
    setPayerName("");
  }, [providerInfo?.FacilityId]);

  const generateClassNameForOutlet = useMemo(() => {
    if (!showSideNav) {
      return "worklists--outlet px-3 pt-6 worklists--outlet__only";
    } else if (showSideNav && !showWorklistNavigation) {
      return "worklists--outlet px-3 pt-6 worklists--outlet__hide-left";
    }
    return "worklists--outlet px-3 pt-6";
  }, [showSideNav, showWorklistNavigation]);

  /**
   * Fetch and cache the current facility name for the worklist sidebar header.
   *
   * Uses the best available facility source (token -> provider -> local store)
   * and gracefully handles missing facility docs to avoid runtime crashes.
   *
   * @returns Promise that resolves after facility name loading completes
   */
  const getHealthFacilityName = async (): Promise<void> => {
    const facilityId =
      getCurrentFacilityId() ||
      providerInfo?.FacilityId ||
      UserStore.getItem(UserGeneralInfoContextKeys.HEALTHCARE_FACILITY_ID);

    if (!facilityId) {
      setHealthFacilityName("");
      return;
    }

    setHealthFacilityLoading(true);
    try {
      const healthFacilityData =
        await FirestoreService.getDocument<HealthCareFacilityModel>(
          FirestoreDocumentReference.healthFacility(facilityId),
        );

      if (healthFacilityData?.Name) {
        setHealthFacilityName(healthFacilityData.Name);
      } else {
        setHealthFacilityName("");
      }
    } catch (error) {
      setHealthFacilityName("");
      console.warn("Unable to load health facility name for worklists", error);
    } finally {
      setHealthFacilityLoading(false);
    }
  };

  return (
    <div className="worklists--layout relative">
      {showSideNav && (
        <div
          className={`flex h-full ${
            showWorklistNavigation
              ? "worklists--navigation"
              : "worklists--navigation__hide"
          }`}
        >
          {/* Sidebar */}
          <div
            className={`worklist--navigation__container flex flex-col overflow-hidden ${
              showWorklistNavigation
                ? "animate-slide-in w-full"
                : "animate-slide-out w-0"
            }`}
          >
            {/* Facility Header */}
            {healthFacilityLoading ? (
              <div>
                <SpinningLoader />
              </div>
            ) : (
              <div className="worklists--navigation__header-container mr-2 mt-2 w-fit max-w-full truncate rounded-2xl bg-primaryGray-16 px-2 py-1 text-tiny font-semibold shadow-md">
                {healthFacilityName}
              </div>
            )}

            {/* Title */}
            <div
              className={`worklists--navigation__header my-3 w-full truncate pl-2 text-lg font-semibold ${
                showWorklistNavigation
                  ? "animate-fade-up"
                  : "animate-fade-up-out"
              }`}
            >
              Worklists
            </div>

            {/* Links */}
            <div
              className={`worklists--navigation__body flex flex-1 flex-col gap-2 overflow-y-auto overflow-x-hidden ${
                showWorklistNavigation
                  ? "animate-fade-down"
                  : "animate-fade-down-out"
              }`}
            >
              {sideLinks.map((sideNavElement, index) => (
                <div
                  key={sideNavElement.id}
                  className={`${
                    showWorklistNavigation
                      ? "animate-stagger-in"
                      : "animate-stagger-out"
                  }`}
                  style={{ transitionDelay: `${10 + index * 50}ms` }}
                >
                  <DynamicSingleSideTab
                    id={sideNavElement.id}
                    label={sideNavElement.label}
                    isActive={activeTab === sideNavElement.id}
                    onTabClick={handleTabClick}
                    shouldShowCount={sideNavElementsIdWhereCountIsRequired.includes(
                      sideNavElement.id as SideNavElementId,
                    )}
                    count={
                      mapOfSideNavElementsIdAndCountOfOrders[sideNavElement.id]
                    }
                    isLoading={countsLoading || reviewPendingCountLoading}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Toggle Icon */}
          <div className="worklist--navigation__icon relative border border-primaryGray-15">
            <div
              className="left-arrow--icon absolute items-center justify-center rounded-full border bg-white p-1 transition-transform duration-300 ease-in-out hover:scale-105 hover:cursor-pointer"
              style={{
                filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.25))",
                left: "-14px",
                top: "22px",
              }}
              onClick={() => setShowWorklistNavigation(!showWorklistNavigation)}
            >
              <ChevronDown rotate={!showWorklistNavigation ? 270 : 90} />
            </div>
          </div>
        </div>
      )}

      {/* Main Outlet */}
      <div className={generateClassNameForOutlet}>
        <Outlet />
      </div>
    </div>
  );
};

export default Worklists;
